import { CheckCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { type DraftPayment, DraftPaymentStatus } from "@/apis/gql/types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type PaymentCardProps = Pick<
  DraftPayment,
  "amount" | "status" | "currency" | "purpose"
> & {
  className?: string;
};

export const PaymentCard = ({
  amount,
  status,
  currency = "EUR",
  purpose,
  className,
}: PaymentCardProps) => {
  const { t } = useTranslation();

  const _statusConfig = {
    sent: {
      label: t("payment_details.webform.status.sent"),
      variant: "success" as const,
      icon: CheckCircle,
    },
    failed: {
      label: t("payment_details.webform.status.failed"),
      variant: "destructive" as const,
      icon: XCircle,
    },
  };

  const _statusConfigMapper = {
    [DraftPaymentStatus.INITIATED]: _statusConfig.sent,
    [DraftPaymentStatus.DRAFT]: null,
  };

  const formattedAmount = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency?.toUpperCase() ?? "EUR",
    minimumFractionDigits: 2,
  }).format(amount ?? 0);

  // FIX ME: need to bring in the payment date from the payment transaction
  // const date = executionDate ?? createdAt;
  // const formattedDate = date ? format(new Date(date), "d.M.yyyy") : null;

  const statusConfig =
    _statusConfigMapper[status ?? DraftPaymentStatus.INITIATED];

  if (!statusConfig) {
    return null;
  }

  const { label, variant } = statusConfig;

  return (
    <Card className={className}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-col gap-2">
          <span
            className={cn(
              "text-xl font-semibold",
              amount && amount < 0 ? "text-destructive" : "text-green-600",
            )}
          >
            {formattedAmount}
          </span>
          <div className="flex flex-row gap-2">
            <Badge variant={variant} className="w-fit gap-1">
              {label}
            </Badge>
            {/* <p className="text-sm text-muted-foreground">am {formattedDate}</p> */}
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold">
              {`${t("payment_details.fields.purpose")}: `}
            </span>
            {purpose}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
